// Vendor Libs
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';

// Dish Tech React
import {useForm, useService, useToast} from '@dishtech-react/hooks';
import {InputTextField} from '@dishtech-react/core';

// Material UI
import CancelIcon from '@material-ui/icons/Cancel';
import SaveIcon from '@material-ui/icons/Save';
import {Button, FormControlLabel, InputLabel, Switch} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';

// Components
import RoleTree from '../role/role-tree';

// Config
import {serviceConfig} from '../../../config/service-config';

const useStyles = makeStyles(theme => ({
    button: {
        margin: theme.spacing(2, 1),
    },
    border: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        '& input': {
            marginLeft: theme.spacing(1),
        },
        '& textarea': {
            marginLeft: theme.spacing(1),
        },
    },
    formControlLabel: {
        display: 'block',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    treeLabel: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    select: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
}));

const FORM_KEY = 'USER';
const ROLE_KEY = 'ROLE';

const UserForm = ({mode, onCancel, onSave}) => {
    const classes = useStyles();
    const {
        formFieldErrors,
        formValid,
        formValues: {username = '', name = '', email = '', active = true, roleIds = []},
        registerForm,
        setFieldValue,
        resetForm,
        submitForm,
    } = useForm(serviceConfig, FORM_KEY, mode);
    const {username: userError, email: emailError} = formFieldErrors;
    const {createToast} = useToast();

    useEffect(() => {
        registerForm();

        return () => resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = () => {
        submitForm({username, name, email, active, roleIds}).then(({error}) => {
            if (error) {
                createToast(error, {
                    variant: 'error',
                    persist: true,
                    onClose: () => onSave(),
                });
            } else {
                createToast(
                    `Successfully ${mode}d user.${
                        mode === 'create' ? ' A password reset link will be sent to the email of user.' : ''
                    }`
                );
                onSave();
            }
        });
    };

    const {
        serviceContent: {content: roleData = []},
    } = useService(ROLE_KEY);

    return (
        <>
            <InputTextField
                autoFocus={true}
                className={classes.border}
                disabled={mode !== 'create'}
                fullWidth={true}
                id="username"
                label={
                    userError !== undefined && username !== '' ? (
                        <div style={{color: 'red'}}>User Name ({userError})</div>
                    ) : (
                        'User Name *'
                    )
                }
                type="text"
                value={username}
                onChange={event => setFieldValue('username', event.target.value)}
            />
            <InputTextField
                autoFocus={false}
                className={classes.border}
                fullWidth={true}
                id="name"
                label="Name *"
                multiline={true}
                type="text"
                value={name}
                onChange={event => setFieldValue('name', event.target.value)}
            />
            <InputTextField
                autoFocus={false}
                className={classes.border}
                fullWidth={true}
                id="email"
                label={
                    emailError !== undefined && email !== '' ? (
                        <div style={{color: 'red'}}>Email ({emailError})</div>
                    ) : (
                        'Email *'
                    )
                }
                multiline={true}
                type="text"
                value={email}
                onChange={event => setFieldValue('email', event.target.value)}
            />
            <FormControlLabel
                className={classes.formControlLabel}
                control={
                    <Switch
                        checked={active}
                        color="primary"
                        id="active"
                        value={active}
                        onChange={event => setFieldValue('active', event.target.checked)}
                    />
                }
                label={`${active ? '' : 'Not'} Active`}
            />
            <InputLabel className={classes.treeLabel} id="roleIds-input-label">
                Roles *
            </InputLabel>
            <RoleTree roleData={roleData} selectedRoles={roleIds} onChange={value => setFieldValue('roleIds', value)} />
            <Button
                className={classes.button}
                color="default"
                startIcon={<CancelIcon />}
                variant="contained"
                onClick={onCancel}
            >
                Cancel
            </Button>
            <Button
                className={classes.button}
                color="primary"
                disabled={!formValid}
                startIcon={<SaveIcon />}
                variant="contained"
                onClick={handleSave}
            >
                {mode}
            </Button>
        </>
    );
};

UserForm.propTypes = {
    mode: PropTypes.string,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
};

UserForm.defaultProps = {
    mode: 'create',
};

export default UserForm;
